/**
 * AMASS PDP — smart sticky buy box (desktop, >=750px).
 * ----------------------------------------------------------------------------
 * The buy box (<amass-product class="amass-pdp__details--sticky">) is a
 * position:sticky grid item beside the stacked image gallery. CSS alone pins
 * its TOP edge below the header, which is exactly right while the box fits in
 * the viewport. The AMASS buy box is usually taller than the viewport (flavor,
 * size and subscription pickers), and a top-pinned box that tall hides its own
 * Add to Cart for the whole gallery scroll. This controller fixes that:
 *
 *   - Box fits the viewport  -> plain top pin (David Protein / Cure Hydration).
 *   - Box taller than viewport, mode "smart" (default):
 *       scrolling DOWN: the box scrolls with the page until its bottom edge is
 *         in view, then pins there (Add to Cart, ship note and accordions stay
 *         on screen while the gallery scrolls past);
 *       scrolling UP: the box scrolls with the page until its top edge is back
 *         under the header, then pins there (title, price and pickers in view).
 *       Either way, once the gallery column ends the whole row scrolls away
 *       together — the grid area is the sticky containing block.
 *   - mode "top": always pin the top edge (reference-site behaviour, even
 *     when that hides the bottom of the box).
 *
 * Implementation: the element stays position:sticky the whole time. Only two
 * CSS values change — the sticky `top` (via --amass-sticky-top) and a
 * margin-top that re-anchors the element's in-flow position to wherever it is
 * on screen at the moment it is released, so there is never a jump. Scroll
 * anchoring is disabled on the element in CSS (overflow-anchor: none) so the
 * browser does not "correct" the scroll position when the margin changes.
 */
(function () {
  'use strict';

  var DESKTOP = window.matchMedia('(min-width: 750px)');
  var PAD = 24; // breathing room above / below the pinned box, px

  function init(panel) {
    var mode = panel.getAttribute('data-amass-sticky-mode') || 'smart';
    if (mode === 'off') return;
    var cell = panel.parentElement;
    if (!cell) return;
    var header = document.querySelector('.amass-header, header-component, .header-section');
    var config = 'top';   // which edge is pinned: 'top' | 'bottom'
    var margin = 0;       // current margin-top re-anchor, px
    var lastY = window.pageYOffset;
    var frame = 0;

    function headerOffset() {
      if (!header) return 0;
      var position = getComputedStyle(header).position;
      if (position !== 'sticky' && position !== 'fixed') return 0;
      return Math.round(header.getBoundingClientRect().height);
    }

    function fits() {
      return panel.offsetHeight + headerOffset() + PAD * 2 <= window.innerHeight;
    }

    function apply(nextConfig, nextMargin) {
      config = nextConfig;
      margin = Math.max(0, Math.round(nextMargin || 0));
      var top = config === 'top'
        ? headerOffset() + PAD
        : window.innerHeight - PAD - panel.offsetHeight;
      panel.style.setProperty('--amass-sticky-top', top + 'px');
      panel.style.marginTop = margin ? margin + 'px' : '';
    }

    function reset() {
      config = 'top';
      margin = 0;
      panel.style.marginTop = '';
      panel.style.removeProperty('--amass-sticky-top');
    }

    function update() {
      frame = 0;
      if (!DESKTOP.matches) {
        if (margin || panel.style.getPropertyValue('--amass-sticky-top')) reset();
        return;
      }
      var y = window.pageYOffset;
      var direction = y > lastY ? 'down' : (y < lastY ? 'up' : null);
      lastY = y;

      // Short box, or explicit top mode: a plain top pin. Keep the offset fresh
      // (header height can change with the announcement bar / viewport).
      if (mode === 'top' || fits()) {
        if (config !== 'top' || margin) { apply('top', 0); return; }
        var wantTop = (headerOffset() + PAD) + 'px';
        if (panel.style.getPropertyValue('--amass-sticky-top') !== wantTop) apply('top', 0);
        return;
      }

      var rect = panel.getBoundingClientRect();
      var cellTop = cell.getBoundingClientRect().top;
      var offTop = headerOffset() + PAD;
      var bottomLine = window.innerHeight - PAD;

      if (config === 'top') {
        // Pinned at the top and the shopper scrolls down: release the box into
        // the flow (anchored where it is) so its bottom edge can come into view.
        if (direction === 'down' && cellTop < offTop && rect.top <= offTop + 1) {
          apply('bottom', rect.top - cellTop);
        }
        return;
      }

      // config === 'bottom'
      if (direction === 'up') {
        if (rect.top >= offTop) {
          // The top edge is back under the header: pin it there.
          apply('top', 0);
        } else if (rect.bottom >= bottomLine - 1) {
          // Pinned at the bottom (or already flowing): anchor in place so the
          // box rides down with the page until its top edge reaches the header.
          var anchor = Math.round(rect.top - cellTop);
          if (anchor !== margin) apply('bottom', anchor);
        }
      }
    }

    function schedule() {
      if (!frame) frame = window.requestAnimationFrame(update);
    }

    function relayout() {
      // Height or viewport changed: re-evaluate from a clean top pin so the
      // sticky offsets are computed against the new size.
      lastY = window.pageYOffset;
      apply('top', 0);
      schedule();
    }

    window.addEventListener('scroll', schedule, { passive: true });
    window.addEventListener('resize', relayout);
    if (DESKTOP.addEventListener) DESKTOP.addEventListener('change', relayout);
    if (typeof window.ResizeObserver === 'function') {
      var lastHeight = panel.offsetHeight;
      new ResizeObserver(function () {
        var h = panel.offsetHeight;
        if (h === lastHeight) return;
        lastHeight = h;
        if (config === 'bottom') {
          // Keep the bottom edge where it is; the box grows/shrinks upward.
          apply('bottom', margin);
        }
        schedule();
      }).observe(panel);
    }
    apply('top', 0);
    schedule();
  }

  function boot() {
    document.querySelectorAll('[data-amass-sticky-panel]').forEach(init);
  }
  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', boot);
  } else {
    boot();
  }
})();
